import React, {useState} from 'react';
import * as yup from 'yup';
import {InputText} from 'primereact/inputtext';
import {Checkbox} from 'primereact/checkbox';
import {Password} from 'primereact/password';
import {Button} from 'primereact/button';
import {classNames} from 'primereact/utils';
import {useFormik} from 'formik';
import {Divider} from 'primereact/divider';
import {useRegisterMutation} from "apis/authenticationApi";
import PhoneInput from 'react-phone-number-input';

const RegisterForm = ({email, token, onRegisterSuccess}) => {
    const [messages, setMessages] = useState("");
    const schema = yup.object({
        password: yup.string().required('Please enter a password')
            .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
                "Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character"
            ),
        passwordConfirmation: yup.string().required('Please enter password again')
            .oneOf([yup.ref('password'), null], 'Confirm password does not match'),
        firstName: yup.string().required("Please enter your first name"),
        lastName: yup.string().required("Please enter your last name"),
        acceptTermsOfUse: yup.bool().oneOf([true], 'Please Accept Terms of Use'),
        acceptPrivacyPolicy: yup.bool().oneOf([true], 'Please Accept Privacy Policy')
    });

    const [register] = useRegisterMutation();

    const formik = useFormik({
        initialValues: {
            email,
            token,
            password: '',
            passwordConfirmation: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            acceptTermsOfUse: false,
            acceptPrivacyPolicy: false
        },
        validationSchema: schema,
        onSubmit: async (data) => {
            const results = await register(data);

            if(results?.data && onRegisterSuccess) {
                onRegisterSuccess();
            }
            else if(results?.error) {
                setMessages(results.error?.data?.message || "System error occurred.  Please try again later")
            }
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    return (
        <form onSubmit={formik.handleSubmit} className="formgrid grid">
            <div className="col-12">
                <span className="error">{messages}</span>
            </div>
            <div className="field col-12">
                <span className="p-float-label">
                    <InputText autoComplete="given-name" id="firstName" name="firstName" className="inputfield w-full" value={formik.values?.firstName} onChange={formik.handleChange} />
                    <label htmlFor="firstName" className={classNames({ 'p-error': isFormFieldValid('firstName') })}>First Name</label>
                </span>
                {getFormErrorMessage('firstName')}
            </div>
            <div className="field col-12">
                <span className="p-float-label">
                    <InputText autoComplete="family-name" id="lastName" name="lastName" className="inputfield w-full" value={formik.values?.lastName} onChange={formik.handleChange} />
                    <label htmlFor="lastName" className={classNames({ 'p-error': isFormFieldValid('lastName') })}>Last Name</label>
                </span>
                {getFormErrorMessage('lastName')}
            </div>
            <div className="field col-12">
                <span className="p-float-label">
                    <PhoneInput autoComplete="tel" placeholder="Phone Number" defaultCountry="US" value={formik.values?.phoneNumber} onChange={(value) => formik.setFieldValue("phoneNumber", value, false)} />
                </span>
                {getFormErrorMessage('phoneNumber')}
            </div>
            <div className="field col-12">
                {email}
            </div>
            <div className="field col-12">
                <span className="p-float-label">
                    <Password autoComplete="new-password" id="password" name="password" className="inputfield w-full" toggleMask value={formik.values?.password} onChange={formik.handleChange} />
                    <label htmlFor="password" className={classNames({ 'p-error': isFormFieldValid('password') })}>Password</label>
                </span>
                {getFormErrorMessage('password')}
            </div>
            <div className="field col-12">
                <span className="p-float-label">
                    <Password autoComplete="new-password" feedback={false} id="passwordConfirmation" name="passwordConfirmation" className="inputfield w-full" toggleMask value={formik.values?.passwordConfirmation} onChange={formik.handleChange} />
                    <label htmlFor="passwordConfirmation" className={classNames({ 'p-error': isFormFieldValid('passwordConfirmation') })}>Confirm Password</label>
                </span>
                {getFormErrorMessage('passwordConfirmation')}
            </div>
            <Divider />
            <div className="field-checkbox col-12">
                <Checkbox inputId="acceptTermsOfUse" name="acceptTermsOfUse" checked={formik.values.acceptTermsOfUse} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('acceptTermsOfUse') })} />
                <label htmlFor="acceptTermsOfUse" className={classNames({ 'p-error': isFormFieldValid('acceptTermsOfUse') })}>I agree to the terms and conditions</label>
            </div>
            <div className="field-checkbox col-12">
                <Checkbox inputId="acceptPrivacyPolicy" name="acceptPrivacyPolicy" checked={formik.values.acceptPrivacyPolicy} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('acceptPrivacyPolicy') })} />
                <label htmlFor="acceptPrivacyPolicy" className={classNames({ 'p-error': isFormFieldValid('acceptPrivacyPolicy') })}>I agree to the privacy policy</label>
            </div>
            <div className="field col-12">
                <Button type="submit" label="Register" />
            </div>
        </form>
    );
}

export default RegisterForm;
